import { EmployeeRecord } from '../../utils/employee-indexeddb';
import { parseDepartmentHierarchy, UNASSIGNED_HEADQUARTER } from '../../utils/department-utils';
import { HeadquarterStats, HeadquarterStatsComparison, ApiKeyUsageData } from './types';
import { normalizeHeadquarterName } from './normalize';
import { debugLog } from '../debug-log';

/**
 * Generate headquarter statistics from employee records
 */
export function generateHeadquarterStats(employees: EmployeeRecord[]): HeadquarterStats[] {
  const hqMap = new Map<string, EmployeeRecord[]>();

  // Group employees by headquarter
  employees.forEach(employee => {
    let headquarter = employee.headquarter;

    if (!headquarter) {
      const parsed = parseDepartmentHierarchy(employee.department);
      headquarter = parsed.headquarter;
    }

    if (!headquarter && employee.department) {
      headquarter = UNASSIGNED_HEADQUARTER;
    }

    if (!headquarter) {
      headquarter = '未設定';
    }

    // Normalize headquarter name (convert half-width katakana to full-width)
    headquarter = normalizeHeadquarterName(headquarter);

    if (!hqMap.has(headquarter)) {
      hqMap.set(headquarter, []);
    }
    hqMap.get(headquarter)!.push(employee);
  });

  // Calculate statistics for each headquarter
  const stats: HeadquarterStats[] = [];

  hqMap.forEach((employeeList, headquarter) => {
    const activeUsers = employeeList.filter(emp => emp.apiKeyId).length;
    const totalUsers = employeeList.length;
    const inactiveUsers = totalUsers - activeUsers;
    const usageRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

    stats.push({
      headquarter,
      usageRate,
      activeUsers,
      inactiveUsers,
      totalUsers,
      totalRequests: 0,
      averageRequests: 0,
      medianRequests: 0,
      totalCost: 0,
      costPerUser: 0,
      costTier500Rate: 0,
      costTier2000Rate: 0,
      costTier4000Rate: 0,
      costTier8000Rate: 0,
      costTier500Users: 0,
      costTier2000Users: 0,
      costTier4000Users: 0,
      costTier8000Users: 0
    });
  });

  // Sort by total cost (descending)
  return stats.sort((a, b) => b.totalCost - a.totalCost);
}

/**
 * Generate headquarter statistics with comparison to previous period
 */
export function generateHeadquarterStatsWithComparison(
  employees: EmployeeRecord[],
  currentPeriodUsage: ApiKeyUsageData[],
  previousPeriodUsage?: ApiKeyUsageData[],
  exchangeRate?: number | null,
  previousPeriodEmployees?: EmployeeRecord[]
): HeadquarterStatsComparison[] {
  const currentStats = generateHeadquarterStatsWithUsageData(employees, currentPeriodUsage, exchangeRate);

  if (!previousPeriodUsage || previousPeriodUsage.length === 0) {
    return currentStats.map(stat => ({ ...stat }));
  }

  const previousStats = generateHeadquarterStatsWithUsageData(previousPeriodEmployees ?? employees, previousPeriodUsage, exchangeRate);
  const previousStatsMap = new Map<string, HeadquarterStats>();
  previousStats.forEach(stat => previousStatsMap.set(stat.headquarter, stat));

  return currentStats.map(currentStat => {
    const previousStat = previousStatsMap.get(currentStat.headquarter);

    if (!previousStat) {
      return { ...currentStat };
    }

    const usageRateDiff = currentStat.usageRate - previousStat.usageRate;
    const activeUsersDiff = currentStat.activeUsers - previousStat.activeUsers;
    const requestsDiff = currentStat.totalRequests - previousStat.totalRequests;
    const costDiff = currentStat.totalCost - previousStat.totalCost;
    const costPerUserDiff = currentStat.costPerUser - previousStat.costPerUser;
    const costTier500RateDiff = currentStat.costTier500Rate - previousStat.costTier500Rate;
    const costTier2000RateDiff = currentStat.costTier2000Rate - previousStat.costTier2000Rate;
    const costTier4000RateDiff = currentStat.costTier4000Rate - previousStat.costTier4000Rate;
    const costTier8000RateDiff = currentStat.costTier8000Rate - previousStat.costTier8000Rate;

    const requestsGrowth = previousStat.totalRequests > 0
      ? ((currentStat.totalRequests - previousStat.totalRequests) / previousStat.totalRequests) * 100
      : 0;

    const costGrowth = previousStat.totalCost > 0
      ? ((currentStat.totalCost - previousStat.totalCost) / previousStat.totalCost) * 100
      : 0;

    return {
      ...currentStat,
      previousPeriod: {
        usageRate: previousStat.usageRate,
        activeUsers: previousStat.activeUsers,
        totalRequests: previousStat.totalRequests,
        totalCost: previousStat.totalCost,
        costPerUser: previousStat.costPerUser
      },
      changes: {
        usageRateDiff,
        activeUsersDiff,
        requestsDiff,
        costDiff,
        costPerUserDiff,
        requestsGrowth,
        costGrowth,
        costTier500RateDiff,
        costTier2000RateDiff,
        costTier4000RateDiff,
        costTier8000RateDiff
      }
    };
  });
}

/**
 * Generate headquarter statistics with API key usage data
 * This version calculates active users based on actual usage in the given period
 */
export function generateHeadquarterStatsWithUsageData(
  employees: EmployeeRecord[],
  apiKeyUsage: ApiKeyUsageData[],
  exchangeRate?: number | null
): HeadquarterStats[] {
  const hqMap = new Map<string, EmployeeRecord[]>();
  const apiKeyMap = new Map<string, ApiKeyUsageData>();

  // Create a map of API keys that have actual usage (requests > 0)
  const activeApiKeys = new Set<string>();

  apiKeyUsage.forEach(apiKey => {
    apiKeyMap.set(apiKey.id, apiKey);
    apiKey.aliases?.forEach(alias => {
      if (!apiKeyMap.has(alias)) {
        apiKeyMap.set(alias, apiKey);
      }
    });
    if (apiKey.requests > 0) {
      activeApiKeys.add(apiKey.id);
    }
  });

  debugLog('=== Stats Generation Debug ===');
  debugLog('Total employees:', employees.length);
  debugLog('Total API keys in usage data:', apiKeyUsage.length);
  debugLog('Active API keys (requests > 0):', activeApiKeys.size);

  // Check for duplicate employees
  const uniqueEmails = new Set(employees.map(e => e.email));
  debugLog('Unique employee emails:', uniqueEmails.size);
  if (uniqueEmails.size !== employees.length) {
    console.warn('⚠️ WARNING: Duplicate employees detected!', employees.length - uniqueEmails.size, 'duplicates');
  }

  // Cost tier thresholds in JPY; convert to USD using exchange rate
  // Fallback to 145.0 (same default as CurrencyManager) when rate is unavailable
  const DEFAULT_EXCHANGE_RATE = 145.0;
  const rate = exchangeRate ?? DEFAULT_EXCHANGE_RATE;
  const costThresholds = {
    tier500: 500 / rate,
    tier2000: 2000 / rate,
    tier4000: 4000 / rate,
    tier8000: 8000 / rate,
  };

  // Group employees by headquarter
  employees.forEach(employee => {
    let headquarter = employee.headquarter;

    if (!headquarter) {
      const parsed = parseDepartmentHierarchy(employee.department);
      headquarter = parsed.headquarter;
    }

    if (!headquarter && employee.department) {
      headquarter = UNASSIGNED_HEADQUARTER;
    }

    if (!headquarter) {
      headquarter = '未設定';
    }

    // Normalize headquarter name (convert half-width katakana to full-width)
    headquarter = normalizeHeadquarterName(headquarter);

    if (!hqMap.has(headquarter)) {
      hqMap.set(headquarter, []);
    }
    hqMap.get(headquarter)!.push(employee);
  });

  // Calculate statistics for each headquarter
  const stats: HeadquarterStats[] = [];

  hqMap.forEach((employeeList, headquarter) => {
    const totalUsers = employeeList.length;

    // Collect request counts and costs for each active user
    const userRequestCounts: number[] = [];
    const userCosts: number[] = [];

    // Count active users based on whether they have an API key AND it was used in this period
    const activeUsers = employeeList.filter(emp => {
      if (!emp.apiKeyId) return false;
      const usage = apiKeyMap.get(emp.apiKeyId);
      if (usage && usage.requests > 0) {
        userRequestCounts.push(usage.requests);
        userCosts.push(usage.cost || 0);
        return true;
      }
      return false;
    }).length;

    const inactiveUsers = totalUsers - activeUsers;
    const usageRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

    // Calculate total requests and cost for this headquarter
    let totalRequests = 0;
    let totalCost = 0;

    employeeList.forEach(employee => {
      if (employee.apiKeyId) {
        const usage = apiKeyMap.get(employee.apiKeyId);
        if (usage) {
          totalRequests += usage.requests || 0;
          totalCost += usage.cost || 0;
        }
      }
    });

    const averageRequests = totalUsers > 0 ? totalRequests / totalUsers : 0;

    // Calculate median requests (only for active users)
    let medianRequests = 0;
    if (userRequestCounts.length > 0) {
      const sorted = [...userRequestCounts].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      medianRequests = sorted.length % 2 !== 0
        ? sorted[mid]
        : (sorted[mid - 1] + sorted[mid]) / 2;
    }

    // Calculate cost-tier usage rates (cost is in USD, thresholds converted above)
    const costTier500Users = userCosts.filter(c => c >= costThresholds.tier500).length;
    const costTier2000Users = userCosts.filter(c => c >= costThresholds.tier2000).length;
    const costTier4000Users = userCosts.filter(c => c >= costThresholds.tier4000).length;
    const costTier8000Users = userCosts.filter(c => c >= costThresholds.tier8000).length;
    // Use totalUsers as denominator to calculate rate against entire organization
    const costTier500Rate = totalUsers > 0 ? (costTier500Users / totalUsers) * 100 : 0;
    const costTier2000Rate = totalUsers > 0 ? (costTier2000Users / totalUsers) * 100 : 0;
    const costTier4000Rate = totalUsers > 0 ? (costTier4000Users / totalUsers) * 100 : 0;
    const costTier8000Rate = totalUsers > 0 ? (costTier8000Users / totalUsers) * 100 : 0;

    const costPerUser = totalUsers > 0 ? totalCost / totalUsers : 0;

    stats.push({
      headquarter,
      usageRate,
      activeUsers,
      inactiveUsers,
      totalUsers,
      totalRequests,
      averageRequests,
      medianRequests,
      totalCost,
      costPerUser,
      costTier500Rate,
      costTier2000Rate,
      costTier4000Rate,
      costTier8000Rate,
      costTier500Users,
      costTier2000Users,
      costTier4000Users,
      costTier8000Users
    });
  });

  // Sort by total cost (descending)
  const sortedStats = stats.sort((a, b) => b.totalCost - a.totalCost);

  // Verify total user count
  const totalUsersInStats = sortedStats.reduce((sum, stat) => sum + stat.totalUsers, 0);
  debugLog('📊 Total users across all HQs:', totalUsersInStats);
  debugLog('📋 Original employee count:', employees.length);
  if (totalUsersInStats !== employees.length) {
    console.error('⚠️ MISMATCH: Total users in stats (' + totalUsersInStats + ') != employee count (' + employees.length + ')');
    console.error('   Difference:', totalUsersInStats - employees.length, 'extra users in stats');
  } else {
    debugLog('✅ User count matches!');
  }

  return sortedStats;
}
